import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  LanguageExerciseProps,
  LanguageExercise as LanguageExerciseType,
  TranslationQuestion,
  ExerciseAnswer,
  AgentEvents,
  LanguageExerciseMarkedEvent,
  GetLanguageExerciseResponse,
  SubmitExerciseAnswersResponse,
} from '@dreamai/types';
import { usePusher } from '../../../../contexts/PusherContext';
import api from '../../../../api';
import './language-exercise.scss';

interface LanguageExerciseComponentProps extends LanguageExerciseProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const LanguageExercise: React.FC<LanguageExerciseComponentProps> = ({
  exerciseId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true) as { userChannel?: any } | null;
  const userChannel = pusherContext?.userChannel;
  const [exercise, setExercise] = useState<LanguageExerciseType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, string>>(new Map());
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [awaitingMarking, setAwaitingMarking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchExercise = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetLanguageExerciseResponse>(
        `/language-learning/exercise/${exerciseId}`,
      );

      if (response.data.success && response.data.exercise) {
        setExercise(response.data.exercise);

        // If already submitted/marked, show results
        if (response.data.exercise.status !== 'in_progress') {
          // Load existing answers
          if (response.data.exercise.answers) {
            const answerMap = new Map<string, string>();
            response.data.exercise.answers.forEach((a: ExerciseAnswer) => {
              answerMap.set(a.questionId, a.answer as string);
            });
            setAnswers(answerMap);
          }
        }
      } else {
        setError(response.data.error || 'Failed to load exercise');
      }
    } catch (err) {
      console.error('Error fetching exercise:', err);
      setError('Failed to load exercise');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, exerciseId]);

  useEffect(() => {
    fetchExercise();
  }, [fetchExercise]);

  // Listen for exercise marked Pusher event
  useEffect(() => {
    if (!userChannel || !exerciseId) return;

    const handleExerciseMarked = (data: LanguageExerciseMarkedEvent) => {
      if (data.exerciseId === exerciseId) {
        console.log('Exercise marked notification received, refetching...');
        setAwaitingMarking(false);
        fetchExercise();
      }
    };

    userChannel.bind(
      AgentEvents.LANGUAGE_EXERCISE_MARKED,
      handleExerciseMarked,
    );

    return () => {
      userChannel.unbind(
        AgentEvents.LANGUAGE_EXERCISE_MARKED,
        handleExerciseMarked,
      );
    };
  }, [userChannel, exerciseId, fetchExercise]);

  // Update visual description
  useEffect(() => {
    if (updateThreadViewVisualDescription && exercise) {
      const status =
        exercise.status === 'marked'
          ? `Marked: ${exercise.score}%`
          : exercise.status === 'submitted'
            ? 'Awaiting marking...'
            : `Question ${currentIndex + 1} of ${exercise.questions.length}`;
      updateThreadViewVisualDescription(`${exercise.title} - ${status}`);
    }
  }, [exercise, currentIndex, updateThreadViewVisualDescription]);

  // Focus input when question changes
  useEffect(() => {
    if (inputRef.current && exercise?.status === 'in_progress') {
      inputRef.current.focus();
    }
  }, [currentIndex, exercise?.status]);

  // Load current answer when navigating
  useEffect(() => {
    if (exercise && currentIndex < exercise.questions.length) {
      const questionId = exercise.questions[currentIndex].id;
      setCurrentAnswer(answers.get(questionId) || '');
    }
  }, [currentIndex, exercise, answers]);

  const currentQuestion = exercise?.questions[currentIndex] as
    | TranslationQuestion
    | undefined;

  const saveCurrentAnswer = () => {
    if (currentQuestion && currentAnswer.trim()) {
      const newAnswers = new Map(answers);
      newAnswers.set(currentQuestion.id, currentAnswer.trim());
      setAnswers(newAnswers);
    }
  };

  const handleNext = () => {
    saveCurrentAnswer();
    if (exercise && currentIndex < exercise.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    saveCurrentAnswer();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && currentAnswer.trim()) {
      if (exercise && currentIndex === exercise.questions.length - 1) {
        // Last question - submit
        handleSubmit();
      } else {
        handleNext();
      }
    }
  };

  const handleSubmit = async () => {
    if (!exercise || isSubmitting) return;

    // Save current answer first
    saveCurrentAnswer();

    // Build final answers array
    const finalAnswers: ExerciseAnswer[] = [];
    exercise.questions.forEach((q) => {
      const answer =
        q.id === currentQuestion?.id ? currentAnswer.trim() : answers.get(q.id);
      finalAnswers.push({
        questionId: q.id,
        answer: answer || '',
      });
    });

    // Check if all questions are answered
    const unanswered = finalAnswers.filter((a) => !a.answer);
    if (unanswered.length > 0) {
      setError(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.post<SubmitExerciseAnswersResponse>(
        `/language-learning/exercise/${exerciseId}/submit`,
        { answers: finalAnswers },
      );

      if (response.data.success && response.data.exercise) {
        setExercise(response.data.exercise);
        setAwaitingMarking(true); // Show waiting state until Pusher notification
      } else {
        setError(response.data.error || 'Failed to submit exercise');
      }
    } catch (err) {
      console.error('Error submitting exercise:', err);
      setError('Failed to submit exercise');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='language-exercise loading'>
        <div className='loading-spinner'>Loading exercise...</div>
      </div>
    );
  }

  if (error && !exercise) {
    return (
      <div className='language-exercise error'>
        <div className='error-message'>{error}</div>
        <button onClick={fetchExercise} className='retry-button'>
          Retry
        </button>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className='language-exercise error'>
        <div className='error-message'>Exercise not found</div>
      </div>
    );
  }

  // Show results if marked
  if (exercise.status === 'marked' && exercise.marking) {
    return (
      <div className='language-exercise marked'>
        <div className='results-header'>
          <h2 className='results-title'>📝 {exercise.title}</h2>
          <div className='score-badge'>
            <span className='score-value'>{exercise.score}%</span>
          </div>
        </div>

        <div className='overall-feedback'>
          <p>{exercise.marking.overallFeedback}</p>
        </div>

        <div className='question-results'>
          {exercise.questions.map((q, index) => {
            const question = q as TranslationQuestion;
            const marking = exercise.marking?.questions.find(
              (m) => m.questionId === question.id,
            );
            const userAnswer =
              answers.get(question.id) || marking?.userAnswer || '';

            return (
              <div
                key={question.id}
                className={`result-item ${marking?.correct ? 'correct' : 'incorrect'}`}
              >
                <div className='result-header'>
                  <span className='question-number'>Q{index + 1}</span>
                  <span
                    className={`result-badge ${marking?.correct ? 'correct' : 'incorrect'}`}
                  >
                    {marking?.correct ? '✓' : '✗'} {marking?.score}%
                  </span>
                </div>
                <div className='result-prompt'>{question.prompt}</div>
                <div className='result-answers'>
                  <div className='user-answer'>
                    <span className='label'>Your answer:</span>
                    <span className='value'>{userAnswer}</span>
                  </div>
                  {!marking?.correct && (
                    <div className='correct-answer'>
                      <span className='label'>Correct:</span>
                      <span className='value'>{marking?.correctAnswer}</span>
                    </div>
                  )}
                </div>
                {marking?.feedback && (
                  <div className='result-feedback'>{marking.feedback}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Show waiting state if submitted but not yet marked
  if (exercise.status === 'submitted') {
    return (
      <div className='language-exercise submitted'>
        <div className='submitted-card'>
          <div className='submitted-icon'>⏳</div>
          <h2>Exercise Submitted!</h2>
          <p>
            Your answers are being reviewed. You'll receive feedback shortly.
          </p>
        </div>
      </div>
    );
  }

  // Active exercise view
  const directionLabel =
    currentQuestion?.direction === 'toTarget'
      ? `English → Target Language`
      : `Target Language → English`;

  const answeredCount = answers.size + (currentAnswer.trim() ? 1 : 0);
  const totalQuestions = exercise.questions.length;

  return (
    <div className='language-exercise'>
      {/* Header */}
      <div className='exercise-header'>
        <h2 className='exercise-title'>{exercise.title}</h2>
        {exercise.instructions && (
          <p className='exercise-instructions'>{exercise.instructions}</p>
        )}
      </div>

      {/* Progress */}
      <div className='exercise-progress'>
        <div className='progress-bar'>
          <div
            className='progress-fill'
            style={{
              width: `${((currentIndex + 1) / totalQuestions) * 100}%`,
            }}
          />
        </div>
        <span className='progress-text'>
          {currentIndex + 1} / {totalQuestions}
        </span>
      </div>

      {/* Question card */}
      {currentQuestion && (
        <div className='question-card'>
          <div className='direction-badge'>{directionLabel}</div>

          {currentQuestion.context && (
            <div className='question-context'>{currentQuestion.context}</div>
          )}

          <div className='question-prompt'>
            <span className='prompt-text'>{currentQuestion.prompt}</span>
          </div>

          {currentQuestion.hint && (
            <div className='question-hint'>
              <span className='hint-label'>💡 Hint:</span>{' '}
              {currentQuestion.hint}
            </div>
          )}

          <div className='answer-area'>
            <input
              ref={inputRef}
              type='text'
              className='answer-input'
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder='Type your translation...'
              disabled={isSubmitting}
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck={false}
            />
          </div>

          {error && <div className='error-message'>{error}</div>}

          {/* Navigation */}
          <div className='navigation-buttons'>
            <button
              className='nav-button prev'
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              ← Previous
            </button>

            {currentIndex < totalQuestions - 1 ? (
              <button
                className='nav-button next'
                onClick={handleNext}
                disabled={!currentAnswer.trim()}
              >
                Next →
              </button>
            ) : (
              <button
                className='nav-button submit'
                onClick={handleSubmit}
                disabled={isSubmitting || answeredCount < totalQuestions}
              >
                {isSubmitting ? 'Submitting...' : 'Submit All'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Answer summary */}
      <div className='answer-summary'>
        <span className='summary-text'>
          {answeredCount} of {totalQuestions} answered
        </span>
        <div className='answer-dots'>
          {exercise.questions.map((q, index) => {
            const isAnswered =
              answers.has(q.id) ||
              (index === currentIndex && currentAnswer.trim());
            const isCurrent = index === currentIndex;
            return (
              <button
                key={q.id}
                className={`answer-dot ${isAnswered ? 'answered' : ''} ${isCurrent ? 'current' : ''}`}
                onClick={() => {
                  saveCurrentAnswer();
                  setCurrentIndex(index);
                }}
                title={`Question ${index + 1}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LanguageExercise;
